import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { 
  PassportIcon, 
  Home01Icon, 
  CheckmarkCircle01Icon, 
  Tick01Icon, 
  Delete02Icon,
  Alert01Icon,
  Time02Icon
} from '@hugeicons/core-free-icons';
import Button from '../components/Button';
import { api } from '../utils/api';
import { FLAGS } from '../config/flags';

const FileUpload = ({ label, icon: Icon, file, onUpload, onRemove }) => (
  <div className="space-y-2">
    <label className="text-xs font-black text-muted-gray uppercase tracking-widest px-1">{label}</label>
    {!file ? (
      <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-3xl cursor-pointer bg-off-white/50 dark:bg-gray-800/50 hover:bg-off-white dark:hover:bg-gray-800 transition-colors group">
        <div className="flex flex-col items-center justify-center pt-5 pb-6">
          <div className="bg-soft-green/10 p-3 rounded-2xl mb-3 text-soft-green group-hover:scale-110 transition-transform">
            <HugeiconsIcon icon={Icon} size={24} />
          </div>
          <p className="text-sm text-charcoal dark:text-gray-300 font-bold">Tap to upload</p>
          <p className="text-[10px] text-muted-gray">JPG, PNG or PDF (Max 10MB)</p>
        </div>
        <input type="file" className="hidden" accept="image/*,application/pdf" onChange={onUpload} />
      </label>
    ) : (
      <div className="relative w-full h-40 rounded-3xl overflow-hidden border border-light-gray/20 dark:border-gray-700/20 bg-off-white dark:bg-gray-800 flex items-center justify-center">
        <div className="text-center">
          <HugeiconsIcon icon={CheckmarkCircle01Icon} size={32} className="text-soft-green mx-auto mb-2" />
          <p className="text-xs font-bold text-charcoal dark:text-white truncate max-w-[200px] px-4">{file.name}</p>
        </div>
        <button 
          onClick={onRemove}
          className="absolute top-3 right-3 p-2 bg-red-500/10 text-red-500 rounded-xl hover:bg-red-500 hover:text-white transition-colors"
        >
          <HugeiconsIcon icon={Delete02Icon} size={16} />
        </button>
      </div>
    )}
  </div>
);

const KYCVerification = () => {
  const [idDoc, setIdDoc] = useState(null);
  const [addressDoc, setAddressDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState('new'); // new, pending, approved, rejected
  const [rejectionReason, setRejectionReason] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStatus = async () => {
      if (FLAGS.USE_MOCK_API) {
        setLoading(false);
        return;
      }

      try {
        const response = await api.get('/kyc/status');
        if (response.data?.kyc) {
          setStatus(response.data.kyc.status); // pending, approved, rejected
          setRejectionReason(response.data.kyc.rejection_reason);
        }
      } catch (err) {
        // 404 is fine, means no KYC yet
        if (err.status !== 404) console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    
    if (FLAGS.USE_MOCK_API) {
      await new Promise(resolve => setTimeout(resolve, 1500));
      setStatus('pending');
      setUploading(false);
      return;
    }

    const formData = new FormData();
    formData.append('government_id', idDoc);
    formData.append('proof_of_address', addressDoc);

    try {
      await api.postFile('/kyc/upload', formData);
      setStatus('pending');
    } catch (err) {
      alert(err.message || 'Failed to upload documents.');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-soft-green"></div>
      </div>
    );
  }

  if (status === 'pending' || status === 'under_review') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-off-white dark:bg-dark-bg">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="bg-yellow-50 dark:bg-yellow-900/20 p-6 rounded-full inline-block animate-pulse">
            <HugeiconsIcon icon={Time02Icon} size={48} className="text-yellow-600 dark:text-yellow-400" />
          </div>
          <h2 className="text-3xl font-black text-charcoal dark:text-white">Under Review</h2>
          <p className="text-muted-gray">We are currently reviewing your documents. You will be notified via email once the process is complete.</p>
          <Button variant="secondary" onClick={() => navigate('/')} className="w-full py-4 rounded-2xl font-black">
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  if (status === 'approved') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-off-white dark:bg-dark-bg">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="bg-green-50 dark:bg-green-900/20 p-6 rounded-full inline-block animate-bounce">
            <HugeiconsIcon icon={Tick01Icon} size={48} className="text-soft-green" />
          </div>
          <h2 className="text-3xl font-black text-charcoal dark:text-white">Verified!</h2>
          <p className="text-muted-gray">Your identity has been successfully verified. You now have full access to all features.</p>
          <Button onClick={() => navigate('/dashboard')} className="w-full py-4 rounded-2xl font-black shadow-float">
            Go to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-off-white dark:bg-dark-bg">
      <div className="max-w-xl w-full bg-white/80 dark:bg-dark-surface/80 backdrop-blur-md p-8 rounded-[2.5rem] border border-light-gray/20 dark:border-dark-border/20 shadow-float-lg">
        <div className="mb-8">
          <h1 className="text-3xl font-black text-charcoal dark:text-white tracking-tight">Identity Verification</h1>
          <p className="text-muted-gray mt-2">Please upload valid documents to verify your identity.</p>
          
          {status === 'rejected' && (
            <div className="mt-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 rounded-2xl flex items-start gap-3">
              <HugeiconsIcon icon={Alert01Icon} className="text-red-500 shrink-0 mt-0.5" size={20} />
              <div>
                <p className="text-sm font-bold text-red-700 dark:text-red-300">Verification Rejected</p>
                <p className="text-xs text-red-600 dark:text-red-400 mt-1">{rejectionReason || "Please upload clearer images and try again."}</p>
              </div>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <FileUpload 
            label="Government ID / Passport" 
            icon={PassportIcon} 
            file={idDoc} 
            onUpload={(e) => setIdDoc(e.target.files[0])} 
            onRemove={() => setIdDoc(null)} 
          />

          <FileUpload 
            label="Proof of Address" 
            icon={Home01Icon} 
            file={addressDoc} 
            onUpload={(e) => setAddressDoc(e.target.files[0])} 
            onRemove={() => setAddressDoc(null)} 
          />

          <div className="pt-4">
            <Button 
              type="submit" 
              disabled={!idDoc || !addressDoc || uploading}
              className="w-full py-4 rounded-2xl text-lg font-black shadow-float disabled:opacity-50"
            >
              {uploading ? 'Uploading...' : 'Submit Documents'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default KYCVerification;
